/**
 * ISG Digital Ecosystem — shared internationalisation.
 *
 * The estate services four locales: en-GB (base), en-US, de, cs. This module
 * is the single JavaScript implementation; `isg_common/i18n.py` is its Python
 * twin (locale list, names, currencies) and the two must stay in step.
 *
 * It replaces two divergent per-app copies — isg-intranet's (base 'en', with
 * fr/es/it stubs nobody asked for) and isg-induction's (base 'en-GB', correct
 * locale set). Induction's was the better of the two and this is largely it,
 * with three things it did not have: a ui-kit chrome layer, correct plural
 * rules, and server-side persistence.
 *
 * ── Two catalogue layers ─────────────────────────────────────────────────
 * ui-kit owns the strings for the furniture every app shares (navigation,
 * modals, toasts, the document admin, the password-change dialog). Apps own
 * their own. Lookup tries the app first, so an app can override a chrome
 * string without forking the component.
 *
 * ── Why the app supplies its own loader ──────────────────────────────────
 * Vite can only code-split a dynamic import whose path it can analyse
 * statically. `import(appPath + locale)` from inside ui-kit is opaque to it,
 * so the app passes a `load` callback containing a literal template import
 * that Vite CAN see. ui-kit loads its own chrome catalogues the same way,
 * from inside this directory.
 *
 *   import enGB from './i18n/en-GB.json';
 *   initI18n({
 *     base: enGB,
 *     load: (locale) => import(`./i18n/locales/${locale}.json`),
 *   });
 */

import { api } from '../services/api.js';
import { DEFAULT_LOCALE, LOCALES, SUPPORTED, normaliseLocale } from './locales.js';
import { setActiveLocale, setTranslator } from './active.js';
import chromeEnGB from './locales/en-GB.json';

export { DEFAULT_LOCALE, LOCALES, SUPPORTED, normaliseLocale };

const STORAGE_KEY = 'isg_locale';

// ── State ────────────────────────────────────────────────────────────────

let currentLocale = DEFAULT_LOCALE;

/** App catalogues, keyed by locale. Populated via initI18n + setLocale. */
const appMessages = {};

/**
 * ui-kit chrome catalogues, keyed by locale.
 *
 * ⚠️ en-GB is STATICALLY bundled, not lazy-loaded like the rest. Thirteen of
 * the estate's apps have not adopted i18n and never call initI18n(), yet they
 * all render ui-kit's navigation, modals and toasts. Were the base catalogue
 * loaded on demand, t() in those apps would return the raw key and they would
 * show "common.cancel" on a button. Bundling it means the shared components
 * are always at worst correct English, and adoption stays genuinely optional.
 */
const chromeMessages = { [DEFAULT_LOCALE]: chromeEnGB };

/** App-supplied loader for its own catalogues. */
let appLoader = null;

/**
 * True once the running app has registered a NON-EMPTY base (en-GB) catalogue
 * of its own — i.e. it has adopted i18n. See hasAppMessages().
 */
let appBaseRegistered = false;

const listeners = [];

/** Locales whose load has been attempted, so a 404 isn't retried on every switch. */
const attempted = new Set();

// ── Public API ───────────────────────────────────────────────────────────

/**
 * Wire up an app's catalogues and resolve the starting locale.
 *
 * @param {object}   opts
 * @param {object}   opts.base    The app's base (en-GB) catalogue, statically imported.
 * @param {function} opts.load    (locale) => Promise<module> for non-base catalogues.
 * @param {string}   [opts.locale] Server-known preference (from /api/user/profile).
 *                                 Wins over localStorage; omit when not signed in.
 * @returns {Promise<string>} the resolved locale.
 */
export async function initI18n({ base, load, locale } = {}) {
  if (base) registerAppMessages(DEFAULT_LOCALE, base);
  if (typeof load === 'function') appLoader = load;

  const resolved = pickInitialLocale(locale);
  await setLocale(resolved, { persist: false });
  return currentLocale;
}

/**
 * Register an app catalogue synchronously.
 *
 * ⚠️ An app MUST call this at module scope with its base catalogue, not rely
 * on passing `base` to initI18n(). Module-level code runs during the import
 * graph, before any boot function — isg-intranet builds its whole NAV_CONFIG
 * with `t('nav.documents')` at import time. If the base catalogue is only
 * registered inside initI18n(), every one of those calls has already returned
 * its own key, and the sidebar renders "nav.documents" in EVERY language,
 * including English. Nothing throws; the console warning scrolls past; the
 * build is clean. It is only visible by opening the page.
 *
 * Registering at module scope is also what the per-app i18n modules this
 * replaced did, which is why the bug did not exist before.
 */
export function registerAppMessages(locale, messages) {
  const code = normaliseLocale(locale);
  if (!code || !messages) return;
  appMessages[code] = { ...(appMessages[code] || {}), ...messages };
  if (code === DEFAULT_LOCALE && Object.keys(messages).length > 0) {
    appBaseRegistered = true;
  }
}

/**
 * Has the running app adopted i18n — registered a base catalogue of its own?
 *
 * Shared components use this to decide whether to OFFER a language at all.
 * The ui-kit chrome is translated in every app, but an app that has not
 * adopted i18n renders its own pages in English whatever is chosen, and
 * offering Czech in a half-English app is worse than not offering it. So the
 * sidebar's language picker renders only when this is true.
 *
 * True once registerAppMessages('en-GB', <non-empty>) has run — which
 * initI18n({ base }) and startAppI18n() both do. An empty catalogue does not
 * count: that is the "looks done" trap (the intranet once shipped `{}`).
 */
export function hasAppMessages() {
  return appBaseRegistered;
}

/**
 * Adopt i18n in one call: register the app's base catalogue and boot the engine
 * with the signed-in user's stored locale.
 *
 *   // <app>/frontend/src/i18n/index.js  (the adapter)
 *   import { registerAppMessages, startAppI18n, t } from '@isg/ui-kit';
 *   import enGB from './en-GB.json';
 *   registerAppMessages('en-GB', enGB);          // ⚠️ AT MODULE SCOPE — see below
 *   export { t };
 *   export const bootI18n = (user) => startAppI18n({
 *     base: enGB,
 *     load: (locale) => import(`./locales/${locale}.json`),   // literal, for Vite
 *     user,
 *   });
 *
 *   // main.js, after the profile has loaded and BEFORE the shell renders:
 *   await bootI18n(store.get('user')).catch(() => {});
 *
 * ⚠️ The app must ALSO call `registerAppMessages('en-GB', base)` at module
 * scope in its own `i18n/index.js` adapter. This helper registers the base too,
 * but only when it runs — at boot, after the import graph has evaluated — and
 * any `t()` evaluated during the import graph (a nav config, a module-level
 * constant) would already have returned its own key. The reference shim is
 * `isg-induction/frontend/src/i18n/index.js`.
 *
 * Existing callers of initI18n() (isg-intranet, isg-induction) are unaffected;
 * this is a thin wrapper over it.
 *
 * @param {object}   opts
 * @param {object}   opts.base    The app's en-GB catalogue, statically imported.
 * @param {function} opts.load    (locale) => Promise<module> for de / cs / en-US.
 * @param {object}   [opts.user]  The signed-in user (its `locale` is the stored
 *                                preference); null/omitted when signed out.
 * @returns {Promise<string>} the resolved locale.
 */
export function startAppI18n({ base, load, user } = {}) {
  if (base) registerAppMessages(DEFAULT_LOCALE, base);
  return initI18n({ base, load, locale: (user && user.locale) || null });
}

/**
 * Translate a key.
 *
 * Lookup order: app[locale] -> chrome[locale] -> app[en-GB] -> chrome[en-GB]
 * -> the key itself.
 *
 * ⚠️ A missing key returns THE KEY, not an empty string — so the common
 * `t('x') || 'fallback'` idiom never falls back. That is deliberate (a
 * visible `settings.language` in the UI is a bug report), but it means the
 * base catalogue must be complete. A key missing only from de/cs falls back
 * to readable English, which is why shipping a partial translation is safe.
 *
 * @param {string} key     Dot-notation, e.g. 'nav.dashboard'.
 * @param {object} [params] Interpolation values. `count` also selects a plural.
 */
export function t(key, params = {}) {
  const value = lookup(key, params);

  if (value === undefined) {
    if (typeof console !== 'undefined') {
      console.warn(`[i18n] Missing key: "${key}" (locale: ${currentLocale})`);
    }
    return key;
  }

  return interpolate(value, params);
}

// Hand `t` to the API client (via active.js) — registration only, nothing is
// translated at module scope.
setTranslator(t);

/** The active locale code, e.g. 'de'. Always one of SUPPORTED. */
export function getLocale() {
  return currentLocale;
}

/**
 * Switch locale. Lazy-loads both catalogues, then notifies listeners.
 *
 * @param {string}  locale
 * @param {object}  [opts]
 * @param {boolean} [opts.persist=true] Write through to localStorage + isg_core.
 */
export async function setLocale(locale, { persist = true } = {}) {
  const next = normaliseLocale(locale) || DEFAULT_LOCALE;

  await Promise.all([loadChrome(next), loadApp(next)]);

  const changed = next !== currentLocale;
  currentLocale = next;
  setActiveLocale(next);

  if (persist) {
    // localStorage first: it is what gives the NEXT page load its first paint
    // in the right language, before any network call resolves.
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* Private browsing / storage disabled — the server copy still carries it. */
    }
    // AWAITED, deliberately. Callers switch language and then reload the page
    // (the only reliable way to repaint a shell and its mounted widgets), and a
    // reload cancels an in-flight request. If the write were fire-and-forget,
    // isg_core would keep the OLD locale, and on the very next load the server
    // value — which outranks localStorage by design — would silently revert the
    // change the user just made. It never rejects, so a failure costs nothing
    // but the round trip.
    await persistToServer(next);
  }

  // Fire even when unchanged on first init so apps can paint once, uniformly.
  if (changed || !listeners.fired) {
    listeners.fired = true;
    listeners.forEach((fn) => {
      try {
        fn(next);
      } catch (err) {
        console.error('[i18n] locale listener failed', err);
      }
    });
  }

  return next;
}

/** Subscribe to locale changes. Returns an unsubscribe function. */
export function onLocaleChange(fn) {
  listeners.push(fn);
  return () => {
    const idx = listeners.indexOf(fn);
    if (idx >= 0) listeners.splice(idx, 1);
  };
}

/** `[{ code, label }]` for a language picker, in display order. */
export function getAvailableLocales() {
  return LOCALES.map(([code, label]) => ({ code, label }));
}

/**
 * Register a chrome catalogue. Used by ui-kit's own bundled locales; exposed
 * so an app can extend or override shared strings without forking a component.
 */
export function registerChromeMessages(locale, messages) {
  const code = normaliseLocale(locale);
  if (!code || !messages) return;
  chromeMessages[code] = { ...(chromeMessages[code] || {}), ...messages };
}

// ── Internals ────────────────────────────────────────────────────────────

/**
 * Decide the starting locale.
 *
 * Precedence: the server's stored value (an explicit choice, and the only one
 * that can follow a user to another app or device) -> localStorage (the same
 * choice, cached for first paint) -> the browser -> en-GB.
 *
 * The browser is consulted ONLY when nothing is stored. A user who chose
 * English must keep it on a machine whose browser is set to German — which is
 * exactly what breaks if you let navigator.languages win unconditionally.
 */
function pickInitialLocale(serverLocale) {
  const fromServer = normaliseLocale(serverLocale);
  if (fromServer) return fromServer;

  let stored = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  const fromStorage = normaliseLocale(stored);
  if (fromStorage) return fromStorage;

  const langs = (typeof navigator !== 'undefined'
    && (navigator.languages || (navigator.language ? [navigator.language] : []))) || [];
  for (const lang of langs) {
    const match = normaliseLocale(lang);
    if (match) return match;
  }

  return DEFAULT_LOCALE;
}

async function loadChrome(locale) {
  if (chromeMessages[locale]) return;
  const cacheKey = `chrome:${locale}`;
  if (attempted.has(cacheKey)) return;
  attempted.add(cacheKey);

  try {
    // Literal template so Vite can glob ./locales/*.json at build time.
    const mod = await import(`./locales/${locale}.json`);
    chromeMessages[locale] = mod.default || mod;
  } catch {
    console.warn(`[i18n] ui-kit chrome catalogue missing for ${locale}`);
    chromeMessages[locale] = {};
  }
}

async function loadApp(locale) {
  if (appMessages[locale] || !appLoader) return;
  const cacheKey = `app:${locale}`;
  if (attempted.has(cacheKey)) return;
  attempted.add(cacheKey);

  try {
    const mod = await appLoader(locale);
    appMessages[locale] = (mod && (mod.default || mod)) || {};
  } catch {
    console.warn(`[i18n] app catalogue missing for ${locale} — falling back to ${DEFAULT_LOCALE}`);
    appMessages[locale] = {};
  }
}

/**
 * Persist to isg_core so the choice follows the user to other apps and devices.
 *
 * Never rejects: being signed out, offline, or running against an app that
 * predates the shared `/api/auth/locale` route must not break a language
 * switch — localStorage already carries it for this origin.
 */
async function persistToServer(locale) {
  try {
    // ⚠️ NO `/api` prefix — the client adds it. This read `/api/auth/locale`
    // and therefore called `/api/api/auth/locale`, which 404s. The catch
    // below is deliberate (a language switch must survive being signed out),
    // so the failure was SILENT: localStorage still carried the choice for
    // that one browser, so it looked like it worked, while isg_core never
    // received it. Measured 2026-08-05: 432 of 433 users had a NULL locale,
    // so nobody's language had ever followed them to another app or device —
    // the entire point of writing it through.
    await api.put('/auth/locale', { locale });
  } catch {
    /* Not signed in, offline, or an older app without the shared route. */
  }
}

function lookup(key, params) {
  const chain = currentLocale === DEFAULT_LOCALE
    ? [appMessages[currentLocale], chromeMessages[currentLocale]]
    : [
      appMessages[currentLocale],
      chromeMessages[currentLocale],
      appMessages[DEFAULT_LOCALE],
      chromeMessages[DEFAULT_LOCALE],
    ];

  for (const catalogue of chain) {
    if (!catalogue) continue;
    const value = resolve(catalogue, key, params);
    if (value !== undefined) return value;
  }
  return undefined;
}

/**
 * Resolve a key within one catalogue, applying plural selection when a
 * `count` is supplied.
 *
 * ⚠️ Plurals go through Intl.PluralRules, NOT a count === 1 test. Czech has
 * FOUR categories — 1 hlas (one), 2 hlasy (few), 5 hlasů (many), 1.5 hlasu
 * (other) — so the one/other split both previous implementations used is
 * simply wrong for it, and produces text a Czech reader reads as broken.
 * German and both Englishes really are one/other, which is why the bug was
 * invisible until Czech arrived.
 */
function resolve(obj, key, params) {
  if (params && params.count !== undefined && params.count !== null) {
    for (const suffix of pluralSuffixes(params.count)) {
      const value = deepGet(obj, key + suffix);
      if (value !== undefined) return value;
    }
  }
  return deepGet(obj, key);
}

function pluralSuffixes(count) {
  let category = 'other';
  try {
    category = new Intl.PluralRules(currentLocale).select(Number(count));
  } catch {
    category = Number(count) === 1 ? 'one' : 'other';
  }
  // Try the exact category, then 'other' as the catch-all a translator can
  // rely on always existing.
  return category === 'other' ? ['_other'] : [`_${category}`, '_other'];
}

/**
 * Look up a dotted key in a catalogue.
 *
 * Handles BOTH catalogue conventions in use across the estate, because the two
 * apps that already had i18n picked different ones: isg-intranet nests
 * (`{"common": {"save": "Save"}}`) and isg-induction is flat with dotted keys
 * (`{"common.save": "Save"}`). A nested-only walk returns undefined for every
 * key in a flat catalogue, so migrating induction would have rendered 154 raw
 * key strings and nothing would have thrown.
 *
 * The literal key is tried first: it is one property access, and a flat
 * catalogue is unambiguous. Only then do we walk the nested path.
 */
function deepGet(obj, path) {
  const literal = obj[path];
  if (typeof literal === 'string') return literal;

  const parts = path.split('.');
  let current = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = current[part];
  }
  return typeof current === 'string' ? current : undefined;
}

/**
 * Replace `{name}` placeholders. Unknown placeholders are left intact.
 *
 * `count` — and only `count` — is formatted through Intl, because it is the
 * designated quantity parameter: "1234 votes" should read "1,234 votes" in
 * English and "1 234 hlasů" in Czech. Every other parameter is stringified
 * verbatim, deliberately. Formatting them all would turn a ticket number or
 * a year into "2 026", which is why this is not applied across the board.
 */
function interpolate(str, params) {
  if (!params) return str;
  return str.replace(/\{(\w+)\}/g, (_, key) => {
    const value = params[key];
    if (value === undefined) return `{${key}}`;
    if (key === 'count' && typeof value === 'number' && Number.isFinite(value)) {
      try {
        return new Intl.NumberFormat(currentLocale).format(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  });
}
